import { unicodes } from './unicodes.js'
import { splitTextInArray } from './array.js'
import { formatBoldLetterToNormal, matchIsUnicodeBold } from './bold.js'
import { compose } from './compose.js'
import { formatItalicLetterToNormal, matchIsUnicodeItalic } from './italic.js'
import { matchIsNumber } from './number.js'
import {
  getUnicodeLetter,
  matchIsCharacterANormalLetter,
  matchIsTextEmpty,
  matchIsTextUnknown,
  matchIsTextUppercase
} from './string.js'

export function matchIsUnicodeLowerBoldItalic(unicode) {
  return (
    (unicode >= unicodes.boldItalic.a && unicode <= unicodes.boldItalic.z) ||
    (unicode >= unicodes.legacyBoldItalic.a &&
      unicode <= unicodes.legacyBoldItalic.z)
  )
}

export function matchIsUnicodeUpperBoldItalic(unicode) {
  return (
    (unicode >= unicodes.boldItalic.A && unicode <= unicodes.boldItalic.Z) ||
    (unicode >= unicodes.legacyBoldItalic.A &&
      unicode <= unicodes.legacyBoldItalic.Z)
  )
}

export function matchIsUnicodeBoldItalic(unicode) {
  return (
    matchIsUnicodeLowerBoldItalic(unicode) ||
    matchIsUnicodeUpperBoldItalic(unicode)
  )
}

export function matchIsLetterBoldItalic(letter) {
  const unicode = getUnicodeLetter(letter)
  if (!matchIsNumber(unicode)) {
    return true
  }
  if (matchIsUnicodeBold(unicode) || matchIsUnicodeItalic(unicode)) {
    return false
  }
  return (
    matchIsUnicodeBoldItalic(unicode) || !matchIsCharacterANormalLetter(letter)
  )
}

export function matchIsTextIsBoldItalic(text) {
  if (matchIsTextEmpty(text) || matchIsTextUnknown(text)) {
    return false
  }
  const textSplitted = splitTextInArray(text)
  return textSplitted.every(matchIsLetterBoldItalic)
}

export function formatNormalLetterToBoldItalic(normalLetter) {
  const unicode = getUnicodeLetter(normalLetter)
  if (!matchIsNumber(unicode) || !matchIsCharacterANormalLetter(normalLetter)) {
    return normalLetter
  }
  if (matchIsTextUppercase(normalLetter)) {
    return String.fromCodePoint(
      unicodes.boldItalic.A - unicodes.normal.A + unicode
    )
  }
  return String.fromCodePoint(
    unicodes.boldItalic.a - unicodes.normal.a + unicode
  )
}

export function formatBoldItalicLetterToNormal(
  boldItalicLetter
) {
  const unicode = getUnicodeLetter(boldItalicLetter)
  if (!matchIsNumber(unicode) || !matchIsUnicodeBoldItalic(unicode)) {
    return boldItalicLetter
  }
  // Normalize from whichever bold-italic block the glyph belongs to (current sans-serif or legacy serif).
  if (
    unicode >= unicodes.legacyBoldItalic.A &&
    unicode <= unicodes.legacyBoldItalic.Z
  ) {
    return String.fromCodePoint(
      unicodes.normal.A - unicodes.legacyBoldItalic.A + unicode
    )
  }
  if (
    unicode >= unicodes.legacyBoldItalic.a &&
    unicode <= unicodes.legacyBoldItalic.z
  ) {
    return String.fromCodePoint(
      unicodes.normal.a - unicodes.legacyBoldItalic.a + unicode
    )
  }
  if (matchIsUnicodeUpperBoldItalic(unicode)) {
    return String.fromCodePoint(
      unicodes.normal.A - unicodes.boldItalic.A + unicode
    )
  }
  return String.fromCodePoint(
    unicodes.normal.a - unicodes.boldItalic.a + unicode
  )
}

export function formatBoldItalic(text) {
  const textSplitted = splitTextInArray(text)
  const textSplittedFormatted = textSplitted.map((letter) => {
    const normalLetter = compose(
      formatBoldItalicLetterToNormal,
      formatBoldLetterToNormal,
      formatItalicLetterToNormal
    )(letter)
    return formatNormalLetterToBoldItalic(normalLetter)
  })
  return textSplittedFormatted.join('')
}
